import { AfterViewInit, Component } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Hub } from 'aws-amplify/utils';
import { AuthSession } from '../../models/profile';
import { first } from 'rxjs';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { LoggerService } from '../../../logging/logger.service';

// Narrow type: list known events + string fallback
export type AuthEventType =
  | 'configured'
  | 'signIn'
  | 'signIn_failure'
  | 'signUp'
  | 'signUp_failure'
  | 'confirmSignUp'
  | 'completeNewPassword_failure'
  | 'autoSignIn'
  | 'autoSignIn_failure'
  | 'forgotPassword'
  | 'forgotPassword_failure'
  | 'forgotPasswordSubmit'
  | 'forgotPasswordSubmit_failure'
  | 'verify'
  | 'tokenRefresh'
  | 'tokenRefresh_failure'
  | 'cognitoHostedUI'
  | 'cognitoHostedUI_failure'
  | 'customOAuthState'
  | 'customState_failure'
  | 'parsingCallbackUrl'
  | 'userDeleted'
  | 'updateUserAttributes'
  | 'updateUserAttributes_failure'
  | 'signOut'
  | string; // allow unknowns gracefully
export interface AuthEventPayload {
  event: AuthEventType;
}

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [SpinnerComponent],
  templateUrl: './callback.component.html',
  styleUrl: './callback.component.scss',
})
export class CallbackComponent implements AfterViewInit {
  authToken: AuthSession | undefined;
  error = '';
  code = '';
  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private logger: LoggerService,
  ) {
    Hub.listen('auth', (data) => {
      const { payload } = data;
      this.onAuthEvent(payload);
    });
  }

  ngAfterViewInit(): void {
    // if it comes to this point when loggged then redirect to dashboard
    this.authService
      .getUserBySession()
      .pipe(first())
      .subscribe((user) => {
        if (user) {
          this.router.navigate(['/auth/redirect']);
        }
      });

    this.route.queryParams.subscribe((params) => {
      this.error = params['error'];
      this.code = params['code'];
      if (this.error) {
        this.logger.error('Callback error', this.error);
      }
      if (this.code) {
        this.navigateToDashboard();
      }
    });
  }

  navigateToDashboard() {
    this.authService
      .getUserBySession()
      .pipe(first())
      .subscribe((user) => {
        this.logger.debug('User by session (callback navigate)', user);
        if (user) {
          this.router.navigate(['/auth/redirect']);
        } else {
          this.router.navigate(['/auth/login']);
        }
      });
  }

  onAuthEvent(payload: AuthEventPayload) {
    const event = payload?.event as AuthEventType;
    switch (event) {
      case 'configured':
        this.logger.info('Az: the Auth module is configured');
        break;
      case 'signIn':
        this.logger.info('Az: user signed in');
        break;
      case 'signIn_failure':
        this.logger.error('Az: user sign in failed');
        break;
      case 'signUp':
        this.logger.info('Az: user signed up');
        break;
      case 'signUp_failure':
        this.logger.error('Az: user sign up failed');
        break;
      case 'confirmSignUp':
        this.logger.info('Az: user confirmation successful');
        break;
      case 'completeNewPassword_failure':
        this.logger.error('Az: user did not complete new password flow');
        break;
      case 'autoSignIn':
        this.logger.info('Az: auto sign in successful');
        break;
      case 'autoSignIn_failure':
        this.logger.error('Az: auto sign in failed');
        break;
      case 'forgotPassword':
        this.logger.info('Az: password recovery initiated');
        break;
      case 'forgotPassword_failure':
        this.logger.error('Az: password recovery failed');
        break;
      case 'forgotPasswordSubmit':
        this.logger.info('Az: password confirmation successful');
        break;
      case 'forgotPasswordSubmit_failure':
        this.logger.error('Az: password confirmation failed');
        break;
      case 'verify':
        this.logger.info('Az: TOTP token verification successful');
        break;
      case 'tokenRefresh':
        this.logger.info('Az: token refresh succeeded');
        break;
      case 'tokenRefresh_failure':
        this.logger.error('Az: token refresh failed');
        break;
      case 'cognitoHostedUI':
        this.logger.info('Az: Cognito Hosted UI sign in successful');
        this.navigateToDashboard();
        break;
      case 'cognitoHostedUI_failure':
        this.logger.error('Az: Cognito Hosted UI sign in failed');
        break;
      case 'customOAuthState':
        this.logger.info('Az: custom state returned from CognitoHosted UI');
        break;
      case 'customState_failure':
        this.logger.error('Az: custom state failure');
        break;
      case 'parsingCallbackUrl':
        this.logger.info('Az: Cognito Hosted UI OAuth url parsing initiated');
        break;
      case 'userDeleted':
        this.logger.info('Az: user deletion successful');
        break;
      case 'updateUserAttributes':
        this.logger.info('Az: user attributes update successful');
        break;
      case 'updateUserAttributes_failure':
        this.logger.error('Az: user attributes update failed');
        break;
      case 'signOut':
        this.logger.info('Az: user signed out');
        break;
      default:
        this.logger.info('Az: unknown event type');
        break;
    }
  }
}
